import { useId } from 'react';
import { Check, Minus, ShieldCheck } from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import {
  categoryOf,
  PERMISSION_CATEGORIES,
  type PermissionCategory,
  type ShopifyPermission,
} from './shopifyScopes';

export interface ShopifyPermissionRow extends Omit<ShopifyPermission, 'category'> {
  /** Derived from the scope handle when omitted. */
  category?: PermissionCategory;
  /** Whether Shopify reported granting this scope. `undefined` is never a
   *  verdict: it means no grant exists yet (nothing is connected), and the row
   *  describes what will be asked for instead of claiming what was given. */
  granted?: boolean;
}

/** The one rendering of Shopify's permissions, shared by the card's "See
 *  permissions" dialog, the Manage popup and the claim dialog. Rows are passed
 *  in, not read from the catalog, because Manage's list is API-driven and can
 *  carry a scope the catalog doesn't name.
 *
 *  Sized below body text on purpose: these used to outrank the sentence the
 *  merchant is actually deciding on. */
export function ShopifyPermissionsList({
  rows,
  labelId,
  descriptionId,
  className,
}: {
  rows: readonly ShopifyPermissionRow[];
  /** id of the caller's own heading, so the list is named without this
   *  component owning the wording. */
  labelId?: string;
  descriptionId?: string;
  className?: string;
}) {
  const idPrefix = useId();
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      className={cn('space-y-3', className)}
    >
      {PERMISSION_CATEGORIES.map((category) => {
        const groupRows = rows.filter(
          (row) => (row.category ?? categoryOf(row.scope)) === category,
        );
        if (groupRows.length === 0) return null;
        const headingId = `${idPrefix}-${category}`;
        return (
          <section key={category} aria-labelledby={headingId} className="space-y-1">
            <h4 id={headingId} className="text-foreground text-caption-medium">
              {GROUP_HEADING[category]}
            </h4>
            {GROUP_NOTE[category] ? (
              <p className="text-muted-foreground text-caption-regular">{GROUP_NOTE[category]}</p>
            ) : null}
            <ul
              // Tailwind preflight sets list-style:none, which makes WebKit drop list
              // semantics entirely; role="list" puts them back for VoiceOver.
              role="list"
              aria-labelledby={headingId}
              className="divide-border border-border divide-y rounded-md border"
            >
              {groupRows.map((row) => (
                <li key={row.scope} className="flex items-start gap-2 px-3 py-2">
                  <PermissionIcon granted={row.granted} />
                  <span className="min-w-0 flex-1 text-caption-regular">
                    <span className="text-foreground font-medium">{row.label}</span>
                    {row.purpose ? (
                      <span className="text-muted-foreground"> — {row.purpose}</span>
                    ) : null}
                  </span>
                  {row.granted === false ? (
                    <span className="shrink-0 text-caption-medium text-amber-700">
                      Not granted
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

const GROUP_HEADING: Record<PermissionCategory, string> = {
  customer_data: 'Customer data',
  store_view: 'Store data: view',
  store_edit: 'Store data: edit',
};

const GROUP_NOTE: Record<PermissionCategory, string | null> = {
  customer_data: 'Page views only, no personal details.',
  store_view: null,
  store_edit: null,
};

function PermissionIcon({ granted }: { granted?: boolean }) {
  if (granted === undefined) {
    return (
      <ShieldCheck className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden="true" />
    );
  }
  return (
    <span
      role={granted ? 'img' : undefined}
      aria-label={granted ? 'Granted' : undefined}
      className={cn(
        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full',
        granted ? 'bg-muted' : 'border-border border border-dashed',
      )}
    >
      {granted ? (
        <Check className="size-2.5" aria-hidden="true" />
      ) : (
        <Minus className="text-muted-foreground size-2.5" aria-hidden="true" />
      )}
    </span>
  );
}
